import type { EditorAiUsageStatus } from './editor-ai';
import type { EditorAiCreditMeter } from './editor-ai-ui';
import type { ResultsAiStreamEvent } from './results-ai';

/**
 * Ask on the Learnings page: questions about a website's own tests, answered from its learning cards,
 * patterns and opportunities, and from any test's results the model chooses to read. Built as the
 * results AI chat is (P-PER-44; decided 2026-10-20 for consistency): one conversation per website,
 * saved and shared by everyone who can open its Learnings, kept LEARNINGS_ASK_RETENTION_MS, one
 * question at a time, streamed into the same docked panel, and counted against the same monthly
 * allowance as the other chats.
 */

/** How long a turn is kept: 12 months, then the daily cleanup deletes it. */
export const LEARNINGS_ASK_RETENTION_MS = 365 * 24 * 60 * 60 * 1000;

/** The newest turns GET /ask/thread returns. Older ones are kept, just not shown. */
export const LEARNINGS_ASK_THREAD_LIMIT = 100;

/**
 * The first-use notice, acknowledged through user_acknowledgements under its own key. Raised whenever
 * it says something new. 2 (2026-10-20): it names the opportunities and the results of tests Ask reads.
 */
export const LEARNINGS_ASK_NOTICE_KEY = 'learnings-ask';
export const LEARNINGS_ASK_NOTICE_VERSION = 2;

export const LEARNINGS_ASK_LIMITS = {
  /** A question, as long as the results chat allows. */
  messageChars: 2_000,
  minChars: 3,
  /** Earlier messages replayed to the model, user and assistant together (six exchanges). */
  historyMessages: 12,
  historyMessageChars: 4_000,
  historyChars: 30_000,
  /** Model calls in one turn: the answer, after at most this many rounds of reading results. */
  toolRounds: 4,
  /** Tests whose results one answer may read. */
  testsPerTurn: 6,
} as const;

/** How long a turn may run, its rounds of reading results included. */
export const LEARNINGS_ASK_TURN_TIMEOUT_MS = 150_000;

/** POST /api/learnings/:websiteId/ask streams the same events as the results chat. */
export type LearningsAskStreamEvent = ResultsAiStreamEvent;

/** One saved turn as GET /ask/thread answers it. Who asked is a key into the response's `authors`. */
export type LearningsAskTurn = {
  turnId: string;
  question: string;
  /** As far as it got: a stopped or failed turn keeps what was written. */
  answer: string;
  status: EditorAiUsageStatus;
  errorMessage: string | null;
  /** Asking again could help (a transient failure): the panel offers Try again. */
  errorRetryable: boolean;
  /** The website's tests the answer names (#1234), each checked to be one of the website's. */
  citations: number[];
  senderId: number;
  /** What the turn was charged, in AI credits, its rounds of reading results included. */
  credits: number;
  /** Tests read since this answer was written: more than 0 marks it as possibly out of date. */
  testsReadSince: number;
  createdAt: number;
};

/**
 * GET /api/learnings/:websiteId/ask/thread. Generic over the author because this package cannot
 * import the database's UserSummary; the server and the dashboard both fill it with that.
 */
export type LearningsAskThreadResponse<Author> = {
  /** Oldest first, the newest LEARNINGS_ASK_THREAD_LIMIT. */
  turns: LearningsAskTurn[];
  /** By user id. Someone whose account is gone is absent. */
  authors: Record<string, Author>;
  /** A question someone is asking now: one runs at a time per website. `mine` when it is the caller's. */
  running: { senderId: number; startedAt: number; mine: boolean } | null;
  /** Whether this user has accepted the notice; Ask refuses until they have. */
  acknowledged: boolean;
  /** The paying account's meter, the chats' one allowance; null for a website with no account. */
  credits: EditorAiCreditMeter | null;
  /** Where to buy a credit pack: only for an account on credits, the one that can. */
  billingUrl: string | null;
};
